import { ActionService } from './actions/action.service';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { CanDeactivateGuard } from './guards/can-deactivate-guard.service';

import { ROUTES } from './app.routes';
import { AppComponent } from './app.component';

import { AuthGuard } from './guards/auth-guard.service';
import { TokenService } from './token.service';
import { AuthService } from './auth.service';
import { PlycaDataService } from '../services/plyca.service';
import { SignatureService } from '../services/signature.service';
import { Configuration } from './conf/app.constants';
import { TaskService } from './tasks/task.service';
import { DocumentService } from './documents/document.service';
import { TemplateService } from './documents/template/template.service';
import { UserService } from './user.service';
import { UIService } from './ui.service';
import { DossierService } from './dossiers/dossier.service';

// App views
import { MessageModule } from './components/message.module';
import { UserModule } from './user/user/user.module';
import { MainViewModule } from './main-view/main-view.module';
import { DocumentFileViewerModule } from './documents/documentFileViewer/documentFileViewer.module';
import { ValidateSignDocumentModule } from './documents/validateSignDocument/validateSignDocument.module';
import { TasksModule } from './tasks/tasks.module';
import { BaseTaskModule } from './tasks/baseTask/baseTask.module';
import { DocumentFileModule } from './documents/documentFile/documentFile.module';
import { WaitLoadingModule } from './components/common/waitLoading/waitLoading.module';
import { ToastModule } from 'primeng-lts/toast';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

// import { LOCALE_ID } from '@angular/core';

/*
 * App State management
 */
import {
  StoreModule,
  ActionReducerMap,
  ActionReducer,
  MetaReducer,
  Store
} from '@ngrx/store';
import { extModules } from './build-specifics';
import { localStorageSync } from 'ngrx-store-localstorage';

// App modules/components
import { LayoutsModule } from './components/common/layout/layouts.module';

// <-- Componente modal

import {
  TranslateModule,
  TranslateLoader,
  TranslateService
} from '@ngx-translate/core';
// --> Dependency injection without constructor
import { ServiceLocator } from './ServiceLocator';
import { Injector } from '@angular/core';
import { MainPipes } from './pipes/main-pipes-module';
import { WelcomeNotesModule } from './components/common/welcomeNotes/welcomeNotes.module';
import { WelcomeNotesService } from './components/common/welcomeNotes/welcomeNotes.component';
import { AppConfiguration } from './conf/app.configuration';
import { NotLoggedInUsersGuard } from './guards/not-loggedin-guard';
import { ProfileGuard } from './guards/profile-guard.service';
import { AppState } from './store/AppState';
import { WaitLoadingService } from './components/common/waitLoading/wait-loading.service';
import { JsonHttpInterceptor } from '../http/json.http-interceptor';
import { JwtHttpInterceptor } from '../http/jwt.http-interceptor';
import { ParseJsonInterceptor } from '../http/parseJson.http-interceptor';
import { HelpService } from './components/common/help/help.service';
import { RuleService } from './dossiers/rules/RuleService';
import { PlycaTranslateHttpLoader } from './i18n/PlycaTranslateHttpLoader';
import { DirectivesModule } from './directives/directives.module';
import { FullCalendarModule } from 'primeng-lts/fullcalendar';
import { PlanningModule } from './dossiers/planning/planning.module';
import { HeaderActionsService } from './components/common/sub-header/header-actions.service';
import { DossierModelsService } from './dossiers/dossierModels/DossierModels.service';
import { I18nHttpInterceptor } from '../http/i18n.http-interceptor';
import { CacheInterceptor } from '../http/cache.http-interceptor';
import { showcontentModule } from './components/showcontent.module';
import { FEGuard } from './guards/fe-guard.service';
import { FENewGuard } from './guards/fe-new-guard.service';
import { PropertyService } from './properties/property.service';
import { BackGroundService } from './login/background.service';
import { AuthGuard1FA } from './guards/auth-guard-1fa.service';
import { ProfileService } from '../services/profile.services';
import { InitService } from '../services/init.service';
import { CacheService } from '../services/cache.service';
import { DossierLinkService } from './dossiers/components/dossierLink.service';
import { LanguageService } from '../services/language.service';
import { NonCacheInterceptor } from '../http/cache-assets.http-interceptor';
import { TableComponentModule } from './main-view/table.module';
import { FormControlName } from '@angular/forms';
import { ConfirmationService, MessageService, PrimeNGConfig } from 'primeng-lts/api';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UniversalDeviceDetectorService } from '../services/universal-device-detector.service';
import { DocumentsModule } from './documents/documents.module';
import { CalendModule } from './calendar/calendar.module';
import { EffectsModule } from '@ngrx/effects';
import { AppStateEffects } from './store/effects/LocalStorageEffect';
import { UserEffects } from './store/effects/userEffects';
import { UserPrefsService } from './store/user-prefs.service';
import { SignatureModule } from '../services/signature/signature.module';
import { SignatureUtils } from '../services/signature.utils';
import { reducers } from './_infrastructure/adapters/store/store.reducer';


export function jwtOptionsFactory(tokenService: TokenService) {
  return {
    tokenGetter: () => {
      return tokenService.getAccessToken();
    }
  };
}
export function jwtHttpInterceptorFactory(
  authService: AuthService,
  http: HttpClient,
  configuration: Configuration,
  tokenService: TokenService
) {
  return new JwtHttpInterceptor(
    authService,
    http,
    configuration,
    tokenService
  );
}

export function i18nHttpInterceptorFactory(languageService: LanguageService) {
  return new I18nHttpInterceptor(languageService);
}

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return (state: AppState, action: any) => localStorageSync({
    keys: ['openedTasks', 'user', 'userPrefs', 'sessionRefresh'],
    syncCondition: () => !action?.avoidPersistence,
    rehydrate: true
  })(reducer)(state, action);
}

const metaReducers: Array<MetaReducer<any, any>> = [localStorageSyncReducer];

export function HttpLoaderFactory(http: HttpClient) {
  return new PlycaTranslateHttpLoader(http, 'assets/i18n/', '.json');
}

export function startupAppConfig(appConfiguration: AppConfiguration, langService: LanguageService, configuration: Configuration,
  userPrefsService: UserPrefsService): Function {

  return () => {
    console.log('startupAppConfig init');
    return appConfiguration.load().then(() => {
      userPrefsService.init();
      langService.init();
      configuration.init();
    })
      .then(() => patchNgFormMemoryLeak());
  };
}

export function signatureUtilsFactory(appConfiguration: AppConfiguration) {
  return new SignatureUtils(appConfiguration);
}

/**
 * Forms Memory leak https://github.com/angular/angular/issues/20007
 */
export function patchNgFormMemoryLeak() {
  const _prototype: any = FormControlName.prototype as any;
  const PATCHED_NAME = '__form_control_memoryleak_patched';
  if (!_prototype[PATCHED_NAME]) {
    const original = _prototype.ngOnDestroy;
    _prototype.ngOnDestroy = function () {
      original && original.apply(this, arguments);
      if (this.control) {
        if (typeof this.control._clearChangeFns === 'function') {
          this.control._clearChangeFns();
        }
        if (typeof this.control.clearValidators === 'function') {
          this.control.clearValidators();
        }
        if (typeof this.control.tmpServerValidators === 'function') {
          this.control.tmpServerValidators = null;
        }
        if (typeof this.control.clearAsyncValidators === 'function') {
          this.control.clearAsyncValidators();
        }
      }
    };
    _prototype[PATCHED_NAME] = true;
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    // Angular modules
    BrowserModule,
    HttpClientModule,
    // Pipes
    MainPipes,
    // Modules
    LayoutsModule,
    FullCalendarModule,
    StoreModule.forRoot(reducers, {
      metaReducers, runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      }
    }),
    EffectsModule.forRoot([AppStateEffects, UserEffects]),
    // https://v12.ngrx.io/guide/store-devtools/recipes/exclude
    extModules,
    ToastModule,
    RouterModule.forRoot(ROUTES, { relativeLinkResolution: 'legacy' }),
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    // Views
    TableComponentModule,
    MainViewModule,
    MessageModule,
    showcontentModule,
    UserModule,
    DocumentFileViewerModule,
    ValidateSignDocumentModule,
    DocumentFileModule,
    TasksModule,
    WaitLoadingModule,
    BaseTaskModule,
    PlanningModule,
    WelcomeNotesModule,
    DirectivesModule,
    DocumentsModule,
    CalendModule,
    SignatureModule
  ],
  providers: [
    { provide: Configuration, useClass: Configuration },
    AppConfiguration,
    {
      provide: LanguageService,
      useClass: LanguageService,
      deps: [AppConfiguration, Store, TranslateService, PrimeNGConfig],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: startupAppConfig,
      deps: [AppConfiguration, LanguageService, Configuration, UserPrefsService],
      multi: true
    },
    { provide: AuthGuard, useClass: AuthGuard },
    { provide: AuthGuard1FA, useClass: AuthGuard1FA },
    { provide: ProfileGuard, useClass: ProfileGuard },
    { provide: FEGuard, useClass: FEGuard },
    { provide: FENewGuard, useClass: FENewGuard },
    NotLoggedInUsersGuard,
    { provide: TokenService, useClass: TokenService },
    { provide: AuthService, useClass: AuthService },
    {
      provide: JwtHttpInterceptor,
      useFactory: jwtHttpInterceptorFactory,
      deps: [AuthService, HttpClient, Configuration, TokenService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: JsonHttpInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: i18nHttpInterceptorFactory,
      deps: [LanguageService],
      multi: true
    },
    {
      provide: SignatureUtils,
      useFactory: signatureUtilsFactory,
      deps: [AppConfiguration]
    },
    { provide: HTTP_INTERCEPTORS, useClass: ParseJsonInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NonCacheInterceptor, multi: true },
    { provide: TaskService, useClass: TaskService },
    { provide: RuleService, useClass: RuleService },
    { provide: PlycaDataService, useClass: PlycaDataService },
    { provide: DocumentService, useClass: DocumentService },
    { provide: TemplateService, useClass: TemplateService },
    { provide: UserService, useClass: UserService },
    { provide: ActionService, useClass: ActionService },
    { provide: HelpService, useClass: HelpService },
    { provide: DossierLinkService, useClass: DossierLinkService },
    { provide: WelcomeNotesService, useClass: WelcomeNotesService },
    { provide: DossierService, useClass: DossierService },
    { provide: DossierModelsService, useClass: DossierModelsService },
    { provide: SignatureService, useClass: SignatureService },
    { provide: ProfileService, useClass: ProfileService },
    { provide: CacheService, useClass: CacheService },
    { provide: InitService, useClass: InitService },
    { provide: UIService, useClass: UIService },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: DialogService, useClass: DialogService },
    { provide: ConfirmationService, useClass: ConfirmationService },
    { provide: DynamicDialogRef, useClass: DynamicDialogRef },
    { provide: DynamicDialogConfig, useClass: DynamicDialogConfig },
    { provide: HeaderActionsService, useClass: HeaderActionsService },
    { provide: BackGroundService, useClass: BackGroundService },
    { provide: LOCALE_ID, useValue: 'es' }, CanDeactivateGuard, WaitLoadingService, PropertyService,
    {
      provide: DeviceDetectorService,
      useClass: UniversalDeviceDetectorService
    },
    MessageService
  ],
  bootstrap: [AppComponent],
  /*Fixing this exception:
  Template parse errors: Can't bind to 'ngModel' since it isn't a known property of
The reason ej:p-dropdown is HTML tag defined as angular component which is custom HTML tag,
not html5 inbuilt tag, so we are using in our component.
if the name of the component contains dash(-), then we need to do follow things.
You can add CUSTOM_ELEMENTS_SCHEMA as Schemas decorator to either in component or module.
I am going to add it app.module.ts file which applies to all components in that module.
  */
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  constructor(private injector: Injector) {
    ServiceLocator.injector = this.injector;
    registerLocaleData(localeEs, 'es');
  }
}
